/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DocumentContact } from 'common/types';
import {
    DOCUMENT_TRADE_SUB_TYPE,
    WORK_ORDER_CONTACT_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { initContact } from 'admin/utils/contact-utils/contact-utils';
import {
    ContactListState,
    ContactOccupancyState,
    createContactDraftReducers,
    createContactListReducers,
    createContactOccupancyReducers,
    isEffectivelyOccupied,
} from 'admin/stores/redux/contactSliceHelpers/contactSliceHelpers';

export interface State extends ContactListState, ContactOccupancyState {}

const newDraftContact = () =>
    initContact(WORK_ORDER_CONTACT_TYPE.CT_GENERAL_CONTRACTOR);

interface StateSlice {
    adminQuoteContactCreation: State;
}

const initialState: State = {
    contacts: [],
    draftContact: newDraftContact(),
    occupied: false,
};

export const adminQuoteContactCreation = createSlice({
    name: 'adminQuoteContactCreation',
    initialState,
    reducers: {
        ...createContactListReducers<State>(),
        ...createContactDraftReducers<State>(newDraftContact),
        ...createContactOccupancyReducers<State>(
            WORK_ORDER_CONTACT_TYPE.CT_OCCUPANT
        ),

        // One action so contacts and occupancy never land in separate renders.
        hydrateFromTemplate: (
            state,
            action: PayloadAction<{
                contacts: DocumentContact[];
                occupied: boolean;
            }>
        ) => {
            state.contacts = action.payload.contacts;
            state.occupied = action.payload.occupied;
        },

        reset: () => ({ ...initialState, draftContact: newDraftContact() }),
    },
});

export const {
    addContact,
    removeContact,
    setDraftContact,
    resetDraftContact,
    setOccupied,
    hydrateFromTemplate,
    reset,
} = adminQuoteContactCreation.actions;

export const quoteContactCreationContacts = (state: StateSlice) =>
    state.adminQuoteContactCreation.contacts;

export const quoteContactCreationDraftContact = (state: StateSlice) =>
    state.adminQuoteContactCreation.draftContact;

export const quoteContactCreationOccupied = (state: StateSlice) =>
    state.adminQuoteContactCreation.occupied;

export const quoteContactCreationEffectiveOccupied =
    (tradeSubTypeId?: DOCUMENT_TRADE_SUB_TYPE) => (state: StateSlice) =>
        isEffectivelyOccupied(
            state.adminQuoteContactCreation.occupied,
            tradeSubTypeId
        );

export default adminQuoteContactCreation.reducer;
